<template>
  <section class="panel">
    <header class="header">
      <strong>Wallets</strong>
      <div class="header-tools">
        <small>{{ sortedWallets.length }}/{{ walletFilter === 'deleted' ? deletedWallets.length : walletFilter === 'all' ? allRows.length : wallets.length }}</small>
        <div class="picker">
          <IconAction name="filter" :label="`Filter: ${walletFilterLabel}`" :active="walletFilter !== 'active'" :size="30" @click="toggleFilterMenu" />
          <div v-if="filterOpen" class="menu" role="menu">
            <button v-for="item in walletFilters" :key="item.key" type="button" :class="{ active: walletFilter === item.key }" @click="selectWalletFilter(item.key)">{{ item.label }}</button>
          </div>
        </div>
        <div class="picker">
          <IconAction name="sort" :label="`Sort: ${sortLabel}`" :active="sortKey !== 'created-desc'" :size="30" @click="toggleSortMenu" />
          <div v-if="sortOpen" class="menu sort-menu" role="menu">
            <button v-for="item in walletSorts" :key="item.key" type="button" :class="{ active: sortKey === item.key }" @click="selectSort(item)">{{ item.label }}</button>
          </div>
        </div>
        <IconAction name="add" :label="showCreate ? 'Hide wallet form' : 'Add wallet'" :active="showCreate" :size="30" :disabled="busyCreate" @click="toggleCreate" />
      </div>
    </header>

    <div class="body">
      <Create v-if="showCreate" :busy="busyCreate" :error="errMsg" @submit="createWallet" @cancel="cancelCreate" />

      <ul class="wallet-grid">
        <Row
          v-for="wallet in activeRows"
          :key="pkOf(wallet)"
          :wallet="wallet"
          :play-state="playStateForWallet(pkOf(wallet))"
          :token-amount="holderAmount(pkOf(wallet))"
          @settings="openWalletSettings"
          @transfer="openTransfer"
        />

        <li v-for="wallet in deletedRows" :key="`deleted:${pkOf(wallet)}`" class="deleted-card">
          <div><strong>{{ wallet.name || 'Wallet' }}</strong><span>{{ shortKey(pkOf(wallet), 5, 5, 16) }}</span></div>
          <button type="button" :disabled="restoreBusy === pkOf(wallet)" @click="restoreDeletedWallet(wallet)">Restore</button>
        </li>

        <li v-if="!activeRows.length && !deletedRows.length" class="empty"><strong>No wallets yet</strong></li>
      </ul>
    </div>

    <ImportModal
      :visible="importModalOpen"
      :public-key="importPk"
      :private-key="importPriv"
      :error="importError"
      @close="closeImport"
    />

    <TransferModal
      :visible="transferOpen"
      source-kind="wallet"
      :source="transferWallet"
      :user="user"
      :wallets="wallets"
      :busy="transferBusy"
      :error="transferError"
      @submit="submitTransfer"
      @close="closeTransfer"
    />

    <div v-if="settingsWallet" class="settings-overlay" @click.self="closeWalletSettings">
      <aside class="settings-card" role="dialog" aria-modal="true" :aria-label="`${settingsWallet.name || 'Wallet'} settings`">
        <header class="settings-head">
          <div>
            <small>GAME ACCOUNT</small>
            <strong>{{ settingsWallet.name || 'Wallet' }}</strong>
            <span>{{ shortKey(pkOf(settingsWallet), 5, 5, 16) }}</span>
          </div>
          <IconAction name="close" label="Close" :size="30" @click="closeWalletSettings" />
        </header>

        <div class="account-settings">
          <label class="name-field"><span>Name</span><input v-model="walletDraft.name" maxlength="64" type="text"></label>
          <label class="toggle-field">
            <span>Autosnipe</span>
            <button type="button" class="toggle" :class="{ on: walletDraft.enabled }" @click="walletDraft.enabled = !walletDraft.enabled">
              {{ walletDraft.enabled ? 'ON' : 'OFF' }}
            </button>
          </label>
          <label><span>Deposit %</span><NumberStepper v-model="walletDraft.pct" :min="1" :max="100" :step="1" /></label>
          <label><span>CUL</span><NumberStepper v-model="walletDraft.cul" :min="0" :step="100000" /></label>
          <label><span>CUP</span><NumberStepper v-model="walletDraft.cup" :min="0" :step="10000" /></label>
          <label><span>TP %</span><NumberStepper v-model="walletDraft.takeProfitPct" :min="0" :step="0.1" /></label>
          <label><span>SL %</span><NumberStepper v-model="walletDraft.stopLossPct" :min="0" :step="0.1" /></label>
          <label><span>TTL sec</span><NumberStepper v-model="walletDraft.ttlSec" :min="0" :step="1" /></label>
          <p v-if="settingsError" class="settings-error">{{ settingsError }}</p>
          <div class="settings-actions">
            <button type="button" @click="openWalletTrades(pkOf(settingsWallet))">Positions</button>
            <button type="button" @click="showPrivate(pkOf(settingsWallet))">Private key</button>
            <button type="button" class="danger" :disabled="!!pendingDelete[pkOf(settingsWallet)]" @click="deleteWalletFromSettings">Delete</button>
            <button type="button" class="save" :disabled="settingsBusy" @click="saveWalletSettings">{{ settingsBusy ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </aside>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue'
import { useStorage } from '@/stores'
import Create from './create.vue'
import Row from './row.vue'
import ImportModal from './import.vue'
import TransferModal from './transfer.vue'
import NumberStepper from '@/components/_shared/NumberStepper.vue'
import IconAction from '@/components/_shared/IconAction.vue'
import { useWalletHolders } from '@/composables/walletHolders'
import { useWalletActions } from '@/composables/walletActions'
import { shortKey } from '@/components/_shared/format'

const props = defineProps({ holders: { type: Array, default: () => [] }, devWallet: { type: String, default: '' } })
const store = useStorage()
const state = computed(() => store.state?.value ?? store.state ?? {})
const { holderAmount } = useWalletHolders(props)

function walletRows(source) {
  const raw = unref(source)
  if (!raw || typeof raw !== 'object') return []
  const entries = Array.isArray(raw)
    ? raw.map(wallet => [wallet?.public_key || wallet?.publicKey || wallet?.PublicKey || '', wallet])
    : Object.entries(raw)
  return entries.map(([key, wallet]) => normalizeWallet(key, unref(wallet))).filter(wallet => wallet.public_key)
}
function normalizeWallet(key, wallet) {
  const source = wallet && typeof wallet === 'object' ? wallet : {}
  const publicKey = String(source.public_key || source.publicKey || source.PublicKey || key || '').trim()
  return {
    ...source,
    public_key: publicKey,
    publicKey,
    name: String(source.name || source.Name || '').trim(),
    lamports: source.lamports ?? source.Lamports ?? source.balance ?? source.Balance ?? 0,
    pnl_lamports: source.pnl_lamports ?? source.pnlLamports ?? source.PnLLamports ?? 0,
    status: source.status || source.Status || '',
    created_at: source.created_at || source.createdAt || source.CreatedAt || '',
    updated_at: source.updated_at || source.updatedAt || source.UpdatedAt || '',
  }
}
function pkOf(wallet) { return String(wallet?.public_key || wallet?.publicKey || wallet?.PublicKey || '').trim() }

const wallets = computed(() => walletRows(store.wallets || state.value.wallets || {}))
const deletedWallets = ref([])
const allRows = computed(() => [...wallets.value, ...deletedWallets.value])
const walletFilter = ref('active')
const filterOpen = ref(false)
const sortOpen = ref(false)
const restoreBusy = ref('')
const walletFilters = [{ key: 'active', label: 'Active' }, { key: 'deleted', label: 'Deleted' }, { key: 'all', label: 'All' }]
const walletFilterLabel = computed(() => walletFilters.find(item => item.key === walletFilter.value)?.label || 'Active')
const visibleWallets = computed(() => {
  if (walletFilter.value === 'deleted') return deletedWallets.value
  if (walletFilter.value === 'all') return allRows.value
  return wallets.value
})

const walletSorts = [
  { key: 'created-desc', label: 'Newest', mode: 'created', dir: -1 },
  { key: 'created-asc', label: 'Oldest', mode: 'created', dir: 1 },
  { key: 'balance-desc', label: 'Balance ↓', mode: 'balance', dir: -1 },
  { key: 'balance-asc', label: 'Balance ↑', mode: 'balance', dir: 1 },
  { key: 'name-asc', label: 'Name A–Z', mode: 'name', dir: 1 },
  { key: 'name-desc', label: 'Name Z–A', mode: 'name', dir: -1 },
]
const sort = computed(() => state.value.settings?.wallets?.sort || { mode: 'created', dir: -1 })
const sortKey = computed(() => {
  const mode = String(sort.value?.mode || 'created') === 'amount' ? 'balance' : String(sort.value?.mode || 'created')
  const dir = Number(sort.value?.dir) < 0 ? -1 : 1
  return walletSorts.find(item => item.mode === mode && item.dir === dir)?.key || 'created-desc'
})
const sortLabel = computed(() => walletSorts.find(item => item.key === sortKey.value)?.label || 'Newest')
function timestamp(value) { const time = Date.parse(String(value || '')); return Number.isFinite(time) ? time : 0 }
const sortedWallets = computed(() => visibleWallets.value.slice().sort((a, b) => {
  const selected = walletSorts.find(item => item.key === sortKey.value) || walletSorts[0]
  const direction = selected.dir
  if (selected.mode === 'name') {
    const result = String(a.name || '').localeCompare(String(b.name || ''))
    return result !== 0 ? result * direction : pkOf(a).localeCompare(pkOf(b))
  }
  const av = selected.mode === 'balance' ? Number(a.lamports || 0) : timestamp(a.created_at)
  const bv = selected.mode === 'balance' ? Number(b.lamports || 0) : timestamp(b.created_at)
  if (av !== bv) return (av - bv) * direction
  return pkOf(a).localeCompare(pkOf(b))
}))
const activeRows = computed(() => sortedWallets.value.filter(item => item.status !== 'deleted'))
const deletedRows = computed(() => sortedWallets.value.filter(item => item.status === 'deleted'))

const {
  showCreate, busyCreate, errMsg, toggleCreate, cancelCreate, createWallet,
  pendingDelete, deleteWallet, importModalOpen, importPk, importPriv, importError,
  openImport, closeImport,
} = useWalletActions(store)

function toggleFilterMenu() { filterOpen.value = !filterOpen.value; sortOpen.value = false }
function toggleSortMenu() { sortOpen.value = !sortOpen.value; filterOpen.value = false }
function selectWalletFilter(value) {
  walletFilter.value = walletFilters.some(item => item.key === value) ? value : 'active'
  filterOpen.value = false
}
async function selectSort(item) {
  sortOpen.value = false
  await store.updateWallets({ sort: { mode: item.mode, dir: item.dir } })
}

const tradeSettings = computed(() => state.value.settings?.trade || store.trade || { owners: {} })
const owners = computed(() => {
  const value = tradeSettings.value?.owners
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
})
const autotrade = computed(() => store.autotrade || state.value.settings?.autotrade || {})
function normalizePct(value) {
  const pct = Math.floor(Number(value))
  return Number.isFinite(pct) ? Math.min(100, Math.max(1, pct)) : 25
}
function ownerConfig(publicKey) {
  const raw = owners.value[String(publicKey || '').trim()]
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw
  if (raw != null) return { pct: normalizePct(raw), selected: true, enabled: true }
  return { pct: 25, selected: false, enabled: true }
}

const runningDraftPluginId = ref(readRunningDraftPluginId())
const selectedWalletKey = computed(() => String(store.selectedWallet?.public_key || '').trim())
const installations = computed(() => {
  const rows = store.uiPluginInstallations || state.value.uiPluginInstallations || []
  return Array.isArray(rows) ? rows : []
})
const playActive = computed(() => installations.value.some(item => item?.active === true && item?.enabled !== false) || Boolean(runningDraftPluginId.value))
function playStateForWallet(publicKey) {
  const key = String(publicKey || '').trim()
  if (!key || !playActive.value || key !== selectedWalletKey.value) return 'off'
  const auto = autotrade.value || {}
  if (auto.enabled === true && String(auto.wallet || '').trim() === key) return 'auto'
  return 'live'
}
function readRunningDraftPluginId() {
  try { return String(JSON.parse(window.localStorage?.getItem('aime.workspace.ownerDraft') || 'null')?.pluginId || '').trim() } catch { return '' }
}
function onDraftPreview(event) { runningDraftPluginId.value = String(event?.detail?.pluginId || '').trim() }
function onDraftPreviewClear() { runningDraftPluginId.value = '' }

const user = computed(() => store.user || state.value.user || {})
const transferOpen = ref(false)
const transferBusy = ref(false)
const transferError = ref('')
const transferSourcePk = ref('')
const transferWallet = computed(() => wallets.value.find(wallet => pkOf(wallet) === transferSourcePk.value) || null)
function openTransfer(publicKey) {
  const key = String(publicKey || '').trim()
  if (!key || transferBusy.value) return
  transferSourcePk.value = key
  transferError.value = ''
  transferOpen.value = true
}
function closeTransfer() { transferOpen.value = false; transferSourcePk.value = ''; transferError.value = '' }
async function submitTransfer(payload) {
  if (transferBusy.value) return
  transferBusy.value = true
  transferError.value = ''
  try { await store.transferWallet(payload); closeTransfer() }
  catch (error) { transferError.value = error?.message || 'transfer_failed' }
  finally { transferBusy.value = false }
}

const settingsPk = ref('')
const walletDraft = ref({})
const settingsBusy = ref(false)
const settingsError = ref('')
const settingsWallet = computed(() => wallets.value.find(wallet => pkOf(wallet) === settingsPk.value) || null)
function openWalletSettings(publicKey) {
  const key = String(publicKey || '').trim()
  if (!key) return
  const wallet = wallets.value.find(item => pkOf(item) === key)
  if (!wallet) return
  const config = ownerConfig(key)
  const auto = autotrade.value || {}
  settingsPk.value = key
  settingsError.value = ''
  walletDraft.value = {
    name: String(wallet.name || '').trim(),
    pct: normalizePct(config.pct),
    cup: Math.max(0, Number(config.cup || 0)),
    cul: Math.max(0, Number(config.cul || 0)),
    takeProfitPct: Math.max(0, Number(config.takeProfitBps || 0) / 100),
    stopLossPct: Math.max(0, Number(config.stopLossBps || 0) / 100),
    ttlSec: Math.max(0, Math.round(Number(config.maxHoldMs || 0) / 1000)),
    enabled: config.enabled !== false && auto.enabled === true && String(auto.wallet || '').trim() === key,
  }
}
function closeWalletSettings() { if (settingsBusy.value) return; settingsPk.value = ''; settingsError.value = '' }
async function saveWalletSettings() {
  const key = settingsPk.value
  const wallet = settingsWallet.value
  if (!key || !wallet || settingsBusy.value) return
  settingsBusy.value = true
  settingsError.value = ''
  try {
    const previous = ownerConfig(key)
    await store.updateTrade({
      owners: {
        ...owners.value,
        [key]: {
          ...previous,
          pct: normalizePct(walletDraft.value.pct),
          cup: Math.max(0, Math.floor(Number(walletDraft.value.cup || 0))),
          cul: Math.max(0, Math.floor(Number(walletDraft.value.cul || 0))),
          takeProfitBps: Math.max(0, Math.floor(Number(walletDraft.value.takeProfitPct || 0) * 100)),
          takeProfitLamports: 0,
          stopLossBps: Math.max(0, Math.floor(Number(walletDraft.value.stopLossPct || 0) * 100)),
          maxHoldMs: Math.max(0, Math.floor(Number(walletDraft.value.ttlSec || 0) * 1000)),
          enabled: walletDraft.value.enabled !== false,
          selected: previous.selected !== false,
        },
      },
    })
    const autoEnabled = walletDraft.value.enabled !== false
    const currentAutoWallet = String(autotrade.value?.wallet || '').trim()
    if (autoEnabled) await store.updateAutotrade({ enabled: true, wallet: key })
    else if (currentAutoWallet === key) await store.updateAutotrade({ enabled: false, wallet: key })

    const name = String(walletDraft.value.name || '').trim()
    if (name && name !== String(wallet.name || '').trim()) await store.renameWallet(key, name)
    settingsPk.value = ''
  } catch (error) {
    settingsError.value = error?.message || 'wallet_settings_save_failed'
  } finally {
    settingsBusy.value = false
  }
}
function showPrivate(publicKey) { settingsPk.value = ''; openImport(publicKey) }
function openWalletTrades(publicKey) {
  settingsPk.value = ''
  window.dispatchEvent(new CustomEvent('aime-shell-wallet-trades', { detail: { wallet: String(publicKey || '').trim() } }))
}
async function deleteWalletFromSettings() {
  const key = settingsPk.value
  if (!key) return
  await deleteWallet(key)
  if (!wallets.value.some(wallet => pkOf(wallet) === key)) settingsPk.value = ''
}

async function loadDeletedWallets() {
  try {
    const result = await store.listWallets(true)
    deletedWallets.value = (Array.isArray(result?.items) ? result.items : [])
      .map(item => normalizeWallet(item?.public_key, item))
      .filter(item => item.status === 'deleted')
  } catch (error) {
    errMsg.value = error?.message || 'wallet_list_failed'
  }
}
async function restoreDeletedWallet(wallet) {
  const key = pkOf(wallet)
  if (!key || restoreBusy.value) return
  restoreBusy.value = key
  try { await store.restoreWallet(key); await loadDeletedWallets() }
  catch (error) { errMsg.value = error?.message || 'wallet_restore_failed' }
  finally { restoreBusy.value = '' }
}

function onWalletSettings(event) { openWalletSettings(event?.detail?.wallet) }

onMounted(() => {
  window.addEventListener('aime-wallet-settings', onWalletSettings)
  window.addEventListener('aime-draft-preview', onDraftPreview)
  window.addEventListener('aime-draft-preview-clear', onDraftPreviewClear)
  void loadDeletedWallets()
})
onBeforeUnmount(() => {
  window.removeEventListener('aime-wallet-settings', onWalletSettings)
  window.removeEventListener('aime-draft-preview', onDraftPreview)
  window.removeEventListener('aime-draft-preview-clear', onDraftPreviewClear)
})
watch(() => wallets.value.length, () => { if (walletFilter.value !== 'active') void loadDeletedWallets() })
</script>

<style scoped>
.panel{height:100%;min-height:0;display:flex;flex-direction:column;border:1px solid #222;border-radius:8px;background:#111;overflow:hidden;color:#e5e7eb}
.header{position:relative;min-height:48px;padding:0 42px 0 12px;display:flex;align-items:center;justify-content:space-between;gap:8px;border-bottom:1px solid #222;background:#1a1a1a}.header>strong{color:#fff;font-size:14px;font-weight:850}
.header-tools{display:flex;align-items:center;gap:7px}.header-tools>small{color:#8fa1b5;font-size:9px;font-variant-numeric:tabular-nums}.picker{position:relative}.menu{position:absolute;z-index:19;right:0;top:36px;min-width:126px;padding:5px;border:1px solid #2b3540;border-radius:9px;background:#090d12f5;box-shadow:0 14px 40px #000c;backdrop-filter:blur(12px)}.menu button{width:100%;min-height:34px;padding:7px 9px;border:0;border-radius:6px;background:transparent;color:#aab4c0;text-align:left;font-size:11px;font-weight:800;cursor:pointer}.menu button.active{background:#10213a;color:#dbeafe}.sort-menu{min-width:138px}
.body{flex:1;min-height:0;padding:8px;overflow-y:auto}.wallet-grid{list-style:none;padding:0;margin:0;display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:8px}.empty{grid-column:1/-1;min-height:150px;display:grid;place-items:center;border:1px dashed #263039;border-radius:8px;color:#cbd5e1}
.deleted-card{min-height:76px;padding:10px;display:flex;align-items:center;justify-content:space-between;gap:10px;border:1px dashed #343434;border-radius:10px;background:#0f0f0f}.deleted-card>div{min-width:0;display:grid;gap:3px}.deleted-card strong{color:#d1d5db;font-size:13px}.deleted-card span{color:#718096;font-size:10px}.deleted-card button,.settings-actions button{height:27px;padding:0 9px;border:1px solid #2b3a4a;border-radius:7px;background:#0d1620;color:#cbd5e1;font-size:9px;font-weight:800;cursor:pointer}
.settings-overlay{position:fixed;inset:0;z-index:9999;display:grid;place-items:center;padding:12px;background:#000c;backdrop-filter:blur(8px)}.settings-card{width:min(560px,100%);max-height:calc(100dvh - 24px);overflow:auto;padding:14px;border:1px solid #203044;border-radius:12px;background:#0a1017;color:#dbe5ef;box-shadow:0 24px 90px #000d}.settings-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}.settings-head>div{min-width:0;display:grid;gap:2px}.settings-head small{color:#7dd3fc;font-size:8px;font-weight:900;letter-spacing:.05em}.settings-head strong{color:#fff;font-size:16px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.settings-head span{color:#8fa1b5;font-size:10px}
.account-settings{margin-top:9px;padding-top:9px;border-top:1px solid #203044;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px}.account-settings label{min-width:0;display:grid;gap:4px}.account-settings label>span{color:#64748b;font-size:8px}.account-settings input{width:100%;height:28px;box-sizing:border-box;border:1px solid #293542;border-radius:6px;background:#080c10;color:#e5e7eb;padding:0 7px;font-size:10px}.name-field{grid-column:span 2}.toggle-field{align-content:start}.toggle{height:28px;border:1px solid #3b4651;border-radius:6px;background:#0d1217;color:#94a3b8;font-size:8px;font-weight:900;cursor:pointer}.toggle.on{border-color:#166534;background:#07170d;color:#86efac}.settings-error{grid-column:1/-1;margin:0;color:#fca5a5;font-size:9px}.settings-actions{grid-column:1/-1;display:flex;justify-content:flex-end;gap:6px;flex-wrap:wrap}.settings-actions .save{border-color:#2563eb;background:#10213a;color:#dbeafe}.settings-actions .danger{border-color:#7f1d1d;background:#2a1111;color:#fecaca}
@media(max-width:560px){.wallet-grid{grid-template-columns:1fr}.account-settings{grid-template-columns:repeat(2,minmax(0,1fr))}.name-field{grid-column:1/-1}.settings-actions{justify-content:stretch}.settings-actions button{flex:1 1 auto}}
</style>
